import { OrganisationInput, OrganisationUpdateValues, UpdateMembershipValues } from "../../../input-types";
import { Organisation, ActiveStatus, User } from "../types";
import { batchFetchItems, getUrl } from "./service-utils";
import { dataToUser } from "./users-service";

const getOrganisationURL = (
    clientId: string | undefined,
    parameters?: string | undefined,
) => {

    return getUrl("organisations", clientId, parameters)
}

export const dataToOrganisation = (data: any): Organisation | null => {
    if (!data) {
        return null
    }

    if (!data.organisationId) {
        console.warn("Tried to convert object without id", data)
        return null
    }

    const organisation: Organisation = {
        organisationId: data.organisationId,
        name: data.name || "",
        type: data.type || ActiveStatus.Inactive,
        meetingViews: data.meetingViews,
        color: data.color || "",
        usersIds: data.usersIds || [],
        adminIds: data.adminIds || [],
        created: data.created ? new Date(data.created) : new Date(),
        clientIds: data.clientIds
    };

    return organisation;
}

/**
 * Lists all organisations
 * @param authToken - Bearer token for authentication
 * @param clientId - The id for the client partition
 * @returns Promise resolving to an array of Organisation objects
 */
const listOrganisations = async (
    authToken: string,
    clientId?: string | undefined,
): Promise<Organisation[]> => {

    if (clientId != undefined && clientId.length == 0) {
        return []
    }

    const url = getOrganisationURL(clientId)

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        const data = await response.json();

        const organisations: Organisation[] = data.map(dataToOrganisation).filter((item: Organisation | null) => item != null);

        return organisations;
    } catch (error) {
        console.log(error);
        throw error as Error;
    }
}

/**
 * Gets a single organisation by its ID
 * @param authToken - Bearer token for authentication
 * @param organisationId - The id of the organisation to fetch
 * @param clientId - The id for the client partition
 * @returns Organisation object or null if not found
 */
const getOrganisation = async (
    authToken: string,
    organisationId: string,
    clientId?: string | undefined,
): Promise<Organisation | null> => {

    if (organisationId.length == 0) {
        return null;
    }

    const url = getOrganisationURL(clientId, organisationId)

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (response.status === 404) {
            console.warn("Organisation not found");
            return null;
        }

        if (!response.ok) {
            console.error("Network response was not ok")
            throw new Error("Network response was not ok");
        }

        const data = await response.json();

        if (data?.organisationId) {
            return dataToOrganisation(data)
        }

        const organisations: Organisation[] = data.map(dataToOrganisation).filter((item: Organisation | null) => item != null);

        if (organisations.length === 0) {
            return null
        }

        return organisations[0];

    } catch (error: unknown) {
        console.log("Failed to fetch organisation", error);
        throw error as Error;
    }
}

/**
 * Fetches multiple organisations by their IDs
 * @param authToken - Bearer token for authentication
 * @param organisationIds - Array of organisation IDs to fetch
 * @param clientId - The id for the client partition
 * @returns Promise resolving to an array of Organisation objects
 */
const getOrganisationWithIds = async (
    authToken: string,
    organisationIds: string[],
    clientId?: string | undefined,
): Promise<(Organisation | null)[]> => {

    return batchFetchItems(
        authToken,
        organisationIds,
        getOrganisationURL,
        dataToOrganisation,
        clientId,
    )
}

/**
 * Adds a new organisation
 * @param authToken - Bearer token for authentication
 * @param organisation - The organisation object to add
 * @param clientId - The id for the client partition
 * @returns Promise resolving to the new organisation's ID or an Error
 */
const addOrganisation = async (
    authToken: string,
    organisation: OrganisationInput,
    clientId?: string | undefined,
): Promise<Organisation | null> => {

    const url = getOrganisationURL(clientId)

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(organisation),
        });

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        const json = await response.json();

        return dataToOrganisation(json)
    } catch (error) {
        console.log(error);
        throw error as Error;
    }
}

/** 
 * Updates an existing organisation
 * @param authToken - Bearer token for authentication
 * @param organisation - The organisation object to update
 * @param clientId - The id for the client partition
 * @returns Promise resolving to a string message or an Error
 */
const updateOrganisation = async ( // TODO can be called replaceOrganisation since it uses PUT which is more like a replace operation
    authToken: string,
    organisationId: string,
    organisation: OrganisationUpdateValues,
    clientId?: string
): Promise<Organisation | null> => {

    const url = getOrganisationURL(clientId, organisationId)

    try {
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(organisation),
        });

        if (!response.ok) {
            throw new Error(`Failed to update organisation: ${response.status} ${response.statusText}`);
        }

        const data = await response.json()

        return dataToOrganisation(data)
    } catch (error) {
        console.log(error);
        throw error;
    }
}

// Example of a PATCH request to update specific fields of an organisation... Use to instead of the full replace above... PUT is more like a 'replace' operation... TODO add for other types
// /**
//  * Updates specific fields of an existing organisation.
//  *
//  * Uses PATCH and only sends the supplied fields.
//  * @param authToken - Bearer token for authentication
//  * @param organisationId - The id of the organisation to update
//  * @param updates - The fields to update
//  * @param clientId - The id for the client partition
//  * @returns Promise resolving to the updated organisation or null
// */
// const updateOrganisation = async (
//     authToken: string,
//     organisationId: string,
//     updates: OrganisationUpdate,
//     clientId?: string
// ): Promise<Organisation | null> => {
//     const url = getOrganisationURL(clientId, organisationId);
//     const response = await fetch(url, {
//         method: "PATCH",
//         headers: {
//             Authorization: `Bearer ${authToken}`,
//             "Content-Type": "application/json",
//         },
//         body: JSON.stringify(updates),
//     });
//     if (!response.ok) {
//         throw new Error(
//             `Failed to update organisation fields: ${response.status} ${response.statusText}`
//         );
//     }
//     const data = await response.json();
//     return dataToOrganisation(data);
// };

// TODO consider making this return void instead of Organisation | null if the deletion is successful
/** 
 * Deletes an organisation
 * @param authToken - Bearer token for authentication
 * @param organisation - The organisation object to delete
 * @param clientId - The id for the client partition
 * @returns Promise resolving to a string message or an Error
 */
const deleteOrganisation = async (
    authToken: string,
    organisationId: string,
    clientId?: string | undefined,
): Promise<void> => {

    const url = getOrganisationURL(clientId, organisationId)

    try {
        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

    } catch (error) {
        console.log(error);
        throw error as Error;
    }
}

/** 
 * Updates an existing organisation
 * @param authToken Bearer token for authentication
 * @param input Required data to update membership
 * - organisationId: the id of organisation to update
 * - targetId: userId of the target to update
 * - membership: whether the user is a member (true), admin (admin) or needs to be removed (false)
 * @param clientId The id for the client partition
 * @returns Promise resolving to a string message or an Error
 */
const updateOrganisationMembership = async (
    authToken: string,
    organisationId: string,
    input: UpdateMembershipValues,
    clientId?: string | undefined
): Promise<{
    organisation: Organisation | null,
    user: User | null
}> => {

    if (!input.targetId || !input.membership || !organisationId) {
        console.error("Invalid Parameters", input)
        throw new Error("Invalid Parameters")
    }

    const url = getOrganisationURL(clientId, `${organisationId}/membership`)

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(input),
        });


        const data = await response.json()
        if (!response.ok) {
            console.error("Failed to change membership", response.status, data)
            throw new Error("Network response was not ok")
        }

        const organisation = dataToOrganisation(data?.organisation)
        const user = dataToUser(data?.user)

        return {
            organisation,
            user
        }
    } catch (error) {
        console.log(error);
        throw error as Error;
    }
}


const OrganisationAPIService = {
    listOrganisations,
    getOrganisation,
    getOrganisationWithIds,
    addOrganisation,
    updateOrganisation,
    deleteOrganisation,
    updateOrganisationMembership,
};

export default OrganisationAPIService;